var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from 'react';
import './App.scss';
// ReactFlowProvider might not be needed here if it's only used within ScenarioEditor
// We keep it for now, but can be moved later for optimization
import { ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import UniversalManager from './components/UniversalManager/UniversalManager';
import { LevelEditor } from './components/LevelEditor/LevelEditor'; // The content editor
import RoleSwitcher from './components/RoleSwitcher/RoleSwitcher';
import Login from './components/Login/Login';
import Tutorial from './components/Tutorial/Tutorial';
import { MANAGER_TOUR, EDITOR_TOUR } from './components/Tutorial/tours';
import { DEMO_DEFAULT_KEY } from '@common/contracts';
import { fetchMe, logout } from './api/auth';
import { getLevels } from './api/levels';
import { getOnboardingStatus, markManagerTutorialSeen, resetOnboarding } from './api/onboarding';
import DbOperationsModal from './components/DbOperationsModal/DbOperationsModal';
import { clearSessionToken, getSessionToken } from './auth/session';
import { getIdentity, setIdentity, identityFromMe, clearIdentity } from './auth/identity';
function App() {
    // State to track which view is active and the currently open level ID
    const [activeView, setActiveView] = useState('manager');
    const [currentLevelId, setCurrentLevelId] = useState(null);
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const [authState, setAuthState] = useState('checking');
    // True only while the manager tour is part of first-run onboarding — then its
    // last "Next" hands off into the editor. A manual replay leaves it false, so
    // each tour runs independently.
    const chainsToEditorRef = useRef(false);
    // A full-screen dim held across the manager → editor handoff so the naked
    // level load (view swap, "Loading…", content pop) never shows: it fades in as
    // the manager tour fades out, and the editor tour's own scrim takes over the
    // moment the editor signals ready.
    const [handoff, setHandoff] = useState(false);
    // While leaving the editor: the editor fades out over the manager (rendered
    // underneath, fully opaque) for a smooth crossfade back, then unmounts.
    const [leavingEditor, setLeavingEditor] = useState(false);
    const leavingRef = useRef(false);
    // Validate the stored session token on boot; an invalid or missing token
    // sends the user to the login screen.
    useEffect(() => {
        if (!getSessionToken()) {
            setAuthState('anonymous');
            return;
        }
        fetchMe()
            .then(me => {
            // Adopt the server's truth: username + admin status + permissions.
            setIdentity(identityFromMe(me));
            setAuthState('authed');
        })
            .catch(() => {
            clearSessionToken();
            clearIdentity();
            setAuthState('anonymous');
        });
    }, []);
    const handleLoggedIn = useCallback(() => {
        // Pull the server-derived identity (admin status + permissions) before the app shows.
        fetchMe()
            .then(me => { setIdentity(identityFromMe(me)); })
            .catch(() => { })
            .finally(() => setAuthState('authed'));
    }, []);
    const handleLogout = useCallback(() => {
        logout().catch(() => { });
        clearSessionToken();
        clearIdentity();
        setActiveView('manager');
        setCurrentLevelId(null);
        setAuthState('anonymous');
    }, []);
    // Auto-open the manager tour for a first-time creator. The "seen" flag lives
    // in the DB (per user, so it follows them across browsers); localStorage is
    // only a fallback if the onboarding API is unreachable.
    useEffect(() => {
        if (authState !== 'authed')
            return;
        let cancelled = false;
        getOnboardingStatus()
            .then(status => {
            if (!cancelled && !status.seenManagerTutorial) {
                chainsToEditorRef.current = true;
                setTutorialOpen(true);
            }
        })
            .catch(() => {
            let seen = false;
            try {
                seen = localStorage.getItem(MANAGER_TOUR.seenKey) === '1';
            }
            catch ( /* ignore */_a) { /* ignore */ }
            if (!cancelled && !seen) {
                chainsToEditorRef.current = true;
                setTutorialOpen(true);
            }
        });
        return () => { cancelled = true; };
    }, [authState]);
    // The Tutorial button lives in the top bar; starting the tour returns to the
    // manager so its highlighted elements exist.
    const handleStartTutorial = useCallback(() => {
        // Manual replay from the top-bar button: manager tour only, no handoff.
        chainsToEditorRef.current = false;
        setActiveView('manager');
        setCurrentLevelId(null);
        setTutorialOpen(true);
    }, []);
    // Manager tour dismissed (skip, close, or finished) — remember it server-side
    // so it doesn't auto-open again.
    const handleTutorialClose = useCallback(() => {
        setTutorialOpen(false);
        markManagerTutorialSeen().catch(() => { });
    }, []);
    // The manager tour's final "Next": hand off into the editor and let its own
    // tour continue. Opens the creator's demo copy — auto-provisioned
    // server-side for every new creator (ensureUserDefaultCopies), so there's
    // always a real Portal to step into.
    const handleEnterEditorTour = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            const username = getIdentity().username;
            const filter = username ? { creator: username } : undefined;
            const levels = yield getLevels(filter);
            const target = (_a = levels.find(l => l.defaultKey === DEMO_DEFAULT_KEY)) !== null && _a !== void 0 ? _a : levels[0];
            if (target) {
                // Swap promptly — the handoff dim already covers the load, so the
                // sooner the editor mounts under it, the sooner it's ready.
                setCurrentLevelId(target.id);
                setActiveView('editor');
            }
            else {
                // Nothing to open — lift the dim so we don't strand the user.
                setHandoff(false);
            }
        }
        catch (err) {
            console.error('Failed to open the tutorial level', err);
            setHandoff(false);
        }
    }), []);
    // The manager tour hands off to the editor ONLY during first-run onboarding
    // (or reset), not on a manual replay from the top-bar button. Raise the dim
    // first so the swap + level load happen under it.
    const handleManagerTourComplete = useCallback(() => {
        if (chainsToEditorRef.current) {
            setHandoff(true);
            handleEnterEditorTour();
        }
    }, [handleEnterEditorTour]);
    // The editor signals it's loaded and has decided on its own tour — drop the
    // handoff dim (the editor tour's scrim, if any, seamlessly continues it).
    const handleEditorReady = useCallback(() => setHandoff(false), []);
    // Safety net: never leave the dim stuck if the editor never reports ready.
    useEffect(() => {
        if (!handoff)
            return;
        const t = window.setTimeout(() => setHandoff(false), 6000);
        return () => window.clearTimeout(t);
    }, [handoff]);
    // Dev "reset onboarding": clears the server record + the local tour flags so
    // both tours play again on the next visit, then reopens the manager tour.
    const handleResetOnboarding = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            yield resetOnboarding();
        }
        catch (err) {
            console.error('Failed to reset onboarding', err);
        }
        try {
            localStorage.removeItem(MANAGER_TOUR.seenKey);
            localStorage.removeItem(EDITOR_TOUR.seenKey);
        }
        catch ( /* ignore */_a) { /* ignore */ }
        // Reset replays the full onboarding, including the editor handoff.
        chainsToEditorRef.current = true;
        setActiveView('manager');
        setCurrentLevelId(null);
        setTutorialOpen(true);
    }), []);
    // The Reset DB dialog: garbage stats + cleanup, and (dev only) the full reset.
    const [resetDbOpen, setResetDbOpen] = useState(false);
    const handleResetDatabase = useCallback(() => setResetDbOpen(true), []);
    // Callback for UniversalManager to trigger opening the editor
    const handleOpenLevelEditor = useCallback((levelId) => {
        setCurrentLevelId(levelId);
        setActiveView('editor');
    }, []);
    // Callback for LevelEditor to go back to the manager. Crossfade instead of a
    // hard cut: keep the editor mounted and fading out over the manager, then swap.
    const handleBackToManager = useCallback(() => {
        if (leavingRef.current)
            return;
        leavingRef.current = true;
        setLeavingEditor(true);
        window.setTimeout(() => {
            setCurrentLevelId(null);
            setActiveView('manager');
            setLeavingEditor(false);
            leavingRef.current = false;
        }, 300);
    }, []);
    // Nothing to render until the stored session has been validated.
    if (authState === 'checking')
        return null;
    if (authState === 'anonymous') {
        return _jsx(Login, { onLoggedIn: handleLoggedIn });
    }
    return (
    // Provider is kept here, needed by LevelEditor (or rather its ScenarioEditor child)
    _jsx(ReactFlowProvider, { children: _jsxs("div", { className: "App app-enter", children: [_jsx(RoleSwitcher, { onStartTutorial: handleStartTutorial, onLogout: handleLogout, onResetOnboarding: handleResetOnboarding, onResetDatabase: handleResetDatabase }), _jsx(DbOperationsModal, { isOpen: resetDbOpen, onClose: () => setResetDbOpen(false) }), (activeView === 'manager' || leavingEditor) && (_jsx(UniversalManager, { onOpenLevel: handleOpenLevelEditor })), activeView === 'editor' && currentLevelId !== null && (
                // Pass the callback to go back and the level ID
                _jsx(LevelEditor, { levelId: currentLevelId, onBack: handleBackToManager, onReady: handleEditorReady, leaving: leavingEditor })), handoff && _jsx("div", { className: "handoff-scrim", "aria-hidden": true }), _jsx(Tutorial, { open: tutorialOpen, tour: MANAGER_TOUR, onClose: handleTutorialClose, onComplete: handleManagerTourComplete })] }) }));
}
export default App;
